import { useCallback, useMemo, useRef, useState } from 'react';

// Lightweight AI-like goal suggestion hook
// API contract:
// - input: { category, duration, difficulty, enabled }
// - returns: { suggestion, request, apply }
//   suggestion shape: { title, durationMin, duration, domain, rationale, reason }
export function useGoalSuggestion({ category = 'digital', duration = 25, difficulty = 'medium', enabled = true } = {}) {
  const [suggestion, setSuggestion] = useState(null);
  const working = useRef(false);

  const domain = category || 'digital';

  const makeSuggestion = useCallback(() => {
    const base = Math.max(5, Math.min(180, Math.round(duration)));
    const diffFactor = { easy: 0.8, medium: 1, hard: 1.2, extreme: 1.4 }[difficulty] ?? 1;
    const suggested = Math.round(base * diffFactor);

    const titles = {
      mental: 'Kısa Meditasyon ve Odak',
      physical: 'Hızlı Esneme ve Hareket',
      social: 'Pozitif Sosyal Bağlantı',
      digital: 'Derin Odak Bloğu'
    };
    const rationales = {
      mental: 'Zihinsel berraklık ve nefes çalışması için önerildi.',
      physical: 'Postür düzeltme ve enerji için kısa bir seri.',
      social: 'İletişim kaslarını canlı tutmak için.',
      digital: 'Dikkati korumak ve akışa girmek için ideal süre.'
    };

    return {
      title: titles[domain] || 'Odak Bloğu',
      durationMin: suggested,
      duration: suggested,
      domain,
      rationale: rationales[domain] || 'Sürdürülebilir ve uygulanabilir bir blok.',
      reason: rationales[domain] || 'Sürdürülebilir ve uygulanabilir bir blok.'
    };
  }, [domain, duration, difficulty]);

  const request = useCallback(() => {
    if (working.current) return;
    working.current = true;
    // simulate async
    const next = makeSuggestion();
    setSuggestion(next);
    working.current = false;
  }, [makeSuggestion]);

  const apply = useCallback(({ onApply } = {}) => {
    const next = suggestion ?? makeSuggestion();
    if (onApply) onApply(next);
  }, [suggestion, makeSuggestion]);

  useMemo(() => {
    if (enabled) {
      // prime with an initial suggestion
      setSuggestion(makeSuggestion());
    }
  }, [enabled, makeSuggestion]);

  return { suggestion, request, apply };
}
